import type { Side } from "../api/types";
import type { ControlKey } from "../state/ui";

/** Lieu de l'exercice des Données initiales (demandes d'Alexis du 03/10/2026, spécification « camp de manœuvre ou
 *  terrain libre ») : deux cartes de choix, « Camp de manœuvre » et « Terrain libre », un champ qui en dépend (liste
 *  des camps, ou liste des terrains de la licence avec « Nouveau terrain »), et un seul bouton qui dit ce qui va
 *  suivre. Les règles sont ici, en fonctions pures testées :
 *  - `layoutOf` : cartes, liste unique (terrain libre non proposé) ou champ CENTAC en lecture seule ;
 *  - `derivedSite` : carte cochée d'après le brouillon et le dossier, tant que l'utilisateur n'en a pas choisi une ;
 *  - `saveKind` : libellé et effet du bouton (créer, enregistrer, placer le terrain…) ; ses préalables manquants
 *    sont ceux des étapes balisées (`missionSteps`), désignés au clic (R12) — jamais un bouton désactivé ;
 *  - `pickSite` : passage d'une carte de choix à l'autre, chacune retrouvant le lieu choisi auparavant ;
 *  - `afterSave` : ce qui suit l'enregistrement — cadrage du dispositif sur un camp ou un terrain prêt (`frame`),
 *    vue Manœuvre à l'étape Terrain pour un nouveau terrain (`terrain`), ou sur l'avancement d'un terrain pas prêt
 *    (`zone-wait`). */

export type Layout = "centac" | "cards" | "list";
export type Site = "camp" | "free";
export type SaveKind = "create" | "create-place" | "save" | "save-place" | "place";
export type MapKind = "camp" | "user" | null;

/** Disposition du bloc : CENTAC (camp imposé), cartes (terrain libre proposé), sinon une seule liste (serveur fermé,
 *  carte mondiale absente, ou état de licence pas encore connu). */
export function layoutOf(o: { centac: boolean; offered: boolean }): Layout {
  if (o.centac) return "centac";
  return o.offered ? "cards" : "list";
}

/** Carte cochée sans choix explicite : « Terrain libre » pour un nouveau terrain choisi ou en attente, un terrain de
 *  la licence, ou un dossier enregistré sans carte ; « Camp de manœuvre » sinon (nouveau dossier compris). */
export function derivedSite(o: { freeTerrain: boolean; pendingFree: boolean; savedNoMap: boolean; mapKind: MapKind }): Site {
  if (o.freeTerrain || o.pendingFree || o.mapKind === "user") return "free";
  if (o.mapKind === "camp") return "camp";
  return o.savedNoMap ? "free" : "camp";
}

/** Libellé et effet du bouton principal. `newTerrain` : « Nouveau terrain » à placer dans la vue Manœuvre ;
 *  `dirtyWithoutChoice` : d'autres champs que ce choix ont changé. */
export function saveKind(o: { saved: boolean; newTerrain: boolean; dirtyWithoutChoice: boolean }): SaveKind {
  if (!o.saved) return o.newTerrain ? "create-place" : "create";
  if (!o.newTerrain) return "save";
  return o.dirtyWithoutChoice ? "save-place" : "place";
}

/** Mémoire de chaque carte de choix pendant la saisie : dernier camp, dernier terrain de la licence (null : « Nouveau
 *  terrain », le défaut de « Terrain libre »). */
export interface SiteMemory {
  camp: string | null;
  terrain: string | null;
}

/** Passage à l'autre carte de choix (clic, toucher, flèches, Début, Fin). Le lieu quitté est retenu, puis :
 *  - « Terrain libre » rend le dernier terrain, sinon « Nouveau terrain » (`target` : "new") ;
 *  - « Camp de manœuvre » rend le dernier camp, à défaut le camp du dossier enregistré, sinon `target` null : la
 *    carte affichée ne change pas, la liste des camps reste vide (le bouton la désignera).
 *  Avec « Nouveau terrain », la carte affichée est celle que le nouveau terrain remplacera : elle n'est pas retenue
 *  comme terrain choisi, et le retour à « Terrain libre » rend « Nouveau terrain ». */
export function pickSite(
  next: Site,
  o: { freeTerrain: boolean; mapKind: MapKind; mapSlug: string | null; savedCamp: string | null },
  memory: SiteMemory,
): { memory: SiteMemory; target: string | "new" | null } {
  if (next === "free") {
    const kept = o.mapKind === "camp" && !o.freeTerrain ? { ...memory, camp: o.mapSlug } : memory;
    return { memory: kept, target: kept.terrain ?? "new" };
  }
  const terrain = o.freeTerrain ? null : o.mapKind === "user" ? o.mapSlug : memory.terrain;
  const kept = { ...memory, terrain };
  return { memory: kept, target: kept.camp ?? o.savedCamp };
}

/** Profil du lieu en bas des Données initiales : celui du lieu choisi pour la carte de choix affichée — ni la carte
 *  qu'un nouveau terrain remplacera, ni un terrain resté affiché sous « Camp de manœuvre » (liste des camps vide), ni
 *  un camp sous « Terrain libre ». */
export function mapProfileShown(o: { newTerrain: boolean; layout: Layout; site: Site; mapKind: MapKind }): boolean {
  if (o.newTerrain) return false;
  if (o.layout !== "cards") return true;
  return o.site === "camp" ? o.mapKind === "camp" : o.mapKind === "user";
}

/** Ce qu'attend la météo « Choisie » (chantier « Données initiales balisées + lieu nommé », 31/10/2026) : `choose` —
 *  un camp ou un terrain à choisir dans la rubrique « Lieu » ; `name` — le lieu du nouveau terrain à nommer (son
 *  climat estimé fait les conditions proposées ; la carte d'avant, que le terrain remplacera, ne compte pas) ; `null`
 *  — un climat connu : camp, terrain de la licence (estimé tant qu'il se prépare), lieu nommé, ou CENTAC (aucun
 *  réglage : la météo de saison se retouche dans l'ordre de la rotation). */
export function weatherWaits(o: {
  centac: boolean;
  newTerrain: boolean;
  mapSlug: string | null;
  /** Lieu du nouveau terrain nommé (`parameters.place`). */
  placeNamed?: boolean;
}): "choose" | "name" | null {
  if (o.centac) return null;
  if (o.newTerrain) return o.placeNamed ? null : "name";
  return o.mapSlug ? null : "choose";
}

/** Le bouton mène-t-il à l'étape Terrain de la vue Manœuvre ? */
export function placesTerrain(kind: SaveKind): boolean {
  return kind === "create-place" || kind === "save-place" || kind === "place";
}

export type AfterSave = "frame" | "terrain" | "zone-wait" | null;

export interface SaveIntent {
  /** Nouveau terrain à placer dans la vue Manœuvre. */
  freeTerrain: boolean;
  /** Carte du dossier avant l'enregistrement (null : nouveau dossier, ou dossier sans carte). */
  previousMap: string | null;
  /** Nouveau dossier (créé par cet enregistrement). */
  created: boolean;
  /** Carte choisie utilisable : un camp, ou un terrain de la licence prêt. */
  mapReady: boolean;
}

/** Ce qui suit l'enregistrement réussi d'un dossier posé sur `savedMap`. */
export function afterSave(intent: SaveIntent, savedMap: string | null): AfterSave {
  if (intent.freeTerrain) return "terrain";
  if (!savedMap) return null;
  if (!intent.created && savedMap === intent.previousMap) return null;
  return intent.mapReady ? "frame" : "zone-wait";
}

/** Mesures de coordination montrées au cadrage d'un camp : fuseau, LIMA, lignes et zones de la ZO (et leurs
 *  noms), graphiques tactiques ; les routes tactiques gardent le réglage de l'utilisateur. */
export const FRAMED_CONTROLS: readonly ControlKey[] = ["fuseau", "lima", "measures", "measureNames", "graphics"];

/** Affichages après le cadrage d'un camp : annotations et unités de tous les camps visibles, le reste inchangé. */
export function annotationsShown(
  controls: Readonly<Record<ControlKey, boolean>>,
  units: Readonly<Record<Side, boolean>>,
): { controlVisible: Record<ControlKey, boolean>; unitsVisible: Record<Side, boolean> } {
  const controlVisible = { ...controls };
  for (const key of FRAMED_CONTROLS) controlVisible[key] = true;
  const unitsVisible = { ...units };
  for (const side of Object.keys(unitsVisible) as Side[]) unitsVisible[side] = true;
  return { controlVisible, unitsVisible };
}

/** Choix « Terrain libre » enregistré sur un dossier qui garde sa carte jusqu'au nouveau terrain (`terrainFor` de
 *  `useZoneDraw`) : la carte « Terrain libre » reste cochée (« Nouveau terrain ») tant que le brouillon n'a pas
 *  choisi une autre carte. */
export function pendingTerrain(
  terrainFor: string | null,
  exerciseId: string | null,
  savedMap: string | null,
  draftMap: string | null,
): boolean {
  return terrainFor !== null && terrainFor === exerciseId && savedMap !== null && draftMap === savedMap;
}
